import { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { Registry } from '../../FormBuilderLibrary';
import { stringToHTML, getElementIcon } from '../utils';
import StyledWrapper from './style';

import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Tooltip from '@mui/material/Tooltip';
import FormControl from '@mui/material/FormControl';

const MUIRadioButton = forwardRef((props, ref) => {
	const { defaultValue, disabled, isFormBuilder, name } = props;

	const [hasError, setHasError] = useState(props?.data?.error || false);
	const [selectedOption, setSelectedOption] = useState(() => {
		// Initialize from defaultValue (radio button only has one selected value)
		if (Array.isArray(defaultValue) && defaultValue.length > 0) {
			return defaultValue[0];
		}
		if (typeof defaultValue === 'string') {
			return defaultValue;
		}
		return '';
	});

	useImperativeHandle(
		ref,
		() => ({
			get value() {
				return selectedOption ? [selectedOption] : [];
			},
			getValue: () => (selectedOption ? [selectedOption] : []),
			getSelectedOption: () => selectedOption
		}),
		[selectedOption]
	);

	const parsedLabel = useMemo(() => stringToHTML(props?.data?.label), [props?.data?.label]);
	const parsedHelperText = useMemo(
		() => stringToHTML(props?.data?.help_message),
		[props?.data?.help_message]
	);

	const handleChange = (optionKey) => {
		setHasError(false);
		setSelectedOption(optionKey);
	};

	const [isOverflowing, setIsOverflowing] = useState(false);
	const labelRef = useRef(null);

	useEffect(() => {
		setHasError(props?.data?.error || false);
	}, [props?.data?.error]);

	useEffect(() => {
		// ARM-397. The form elements initially take up the entire width of the screen and then are resized to fit into the proper row size. This setTimeout adds a delay so we do this calculation after the form elements are set to their proper row width. Without this the scroll width and clientWidth will be the same on screens with a width of 2000px and above
		setTimeout(() => {
			if (labelRef.current) {
				setIsOverflowing(labelRef.current.scrollWidth > labelRef.current.clientWidth);
			}
		}, 100);
	});

	const renderRadioGroup = (withMarginTop = false) => {
		const OutlineComponent = withMarginTop
			? StyledWrapper.BorderOutlineMarginTop
			: StyledWrapper.BorderOutline;

		return (
			<OutlineComponent
				error={hasError}
				className={'customInputOutline'}
				disabled={disabled ? true : false}
			>
				<RadioGroup
					aria-labelledby="demo-radio-buttons-group-label"
					value={selectedOption}
					name="radio-buttons-group"
					error={hasError}
				>
					<div
						style={{
							display: 'flex',
							margin: '0px',
							flexDirection: `${props?.data?.inline ? 'row' : 'column'}`
						}}
					>
						{props?.data?.options.map((option) => {
							const this_key = `preview_${option.key}`;
							const isDisabled = props.read_only || disabled;

							return (
								<div key={`RadioGroup-${option.key}`}>
									<FormControlLabel
										className="disableWhiteBackground noMarginTop"
										style={{ marginBottom: '0px' }}
										disabled={isDisabled}
										control={
											<Radio
												id={`fid_${this_key}`}
												name={`option_${option.key}`}
												value={option.key}
												checked={selectedOption === option.key}
												onChange={() => handleChange(option.key)}
												disabled={isDisabled}
												inputProps={{
													'data-value': option?.value
												}}
											/>
										}
										label={option?.text}
									/>
								</div>
							);
						})}
					</div>
				</RadioGroup>
			</OutlineComponent>
		);
	};

	return (
		<>
			<FormControl fullWidth name={name}>
				<Tooltip
					arrow
					placement="top"
					title={isOverflowing ? `${props?.data?.label}` : ''}
					enterTouchDelay={0}
				>
					<StyledWrapper.CustomLabel
						error={hasError}
						value={selectedOption !== ''}
						disabled={disabled ? true : false}
						isOverflowing={isOverflowing}
						required={props?.data?.required}
						htmlFor="outlined-adornment-password"
						shrink={true}
					>
						<StyledWrapper.LabelContents ref={labelRef}>
							{isFormBuilder && getElementIcon(props.data?.key)}
							{parsedLabel?.[0] ? parsedLabel : props?.data?.label}
							{isFormBuilder && (
								<span>
									{' '}
									{props.data?.unique_identifier
										? `(ff-${props.data?.unique_identifier})`
										: `(tmp-${props.data?.temp_order})`}
								</span>
							)}
						</StyledWrapper.LabelContents>
					</StyledWrapper.CustomLabel>
				</Tooltip>
				{isFormBuilder ? renderRadioGroup(false) : renderRadioGroup(true)}
			</FormControl>
			<StyledWrapper.FormHelperTextContainer>
				<FormHelperText error={hasError}>
					{' '}
					{props.data.error?.error ? `${props.data.label} ${props.data.error?.error}` : ''}
				</FormHelperText>
				{parsedHelperText && <FormHelperText>{parsedHelperText}</FormHelperText>}
			</StyledWrapper.FormHelperTextContainer>
		</>
	);
});

MUIRadioButton.displayName = 'MUI_RadioButton';
Registry.register('MUI_RadioButton', MUIRadioButton);

export default MUIRadioButton;
